import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder } from '@/shared/api';
import type { ErrorResponse } from '@/shared/api/types';

const palletsEndpoint = createEndpointBuilder('/api/v2/consignments');

/**
 * Cache tags of one consignment's pallet data.
 *
 * The pallet group and the consignment lines (which list the pallets with their
 * type and capacity) provide and invalidate this exact tag, so a pallet change
 * refetches both and a line change refetches the group. The params shape/order
 * must match `getConsignmentLines`: `{ projectId, siteId, consignmentId }`.
 */
const consignmentLinesTags = ({
  consignmentId,
  projectId,
  siteId
}: {
  consignmentId: string;
  projectId?: number;
  siteId?: number;
}) =>
  createCacheTags({
    baseTag: API_TAGS.CONSIGNMENTS,
    params: { projectId, siteId, consignmentId },
    includeBaseTag: false
  });

// ─── Constants ────────────────────────────────────────────────────────────────

/**
 * Pallet type ids (`overpacking_type.id`).
 * Labels come from the legacy `overpackingType.<id>` translations.
 */
export const PALLET_TYPE_IDS = {
  WAREHOUSE_PALLET: 1,
  WOODEN_CRATE: 3
} as const;

export type PalletTypeId = (typeof PALLET_TYPE_IDS)[keyof typeof PALLET_TYPE_IDS];

// ─── Request/Response Types ──────────────────────────────────────────────────

export interface PalletGroupParams {
  consignmentId: string;
  projectId?: number;
  siteId?: number;
}

/** One pallet of the consignment's pallet group */
export interface PalletGroupUnit {
  /** Pallet type id, as a string */
  type: string | null;
  /** Stable across saves; generated by the client for a new pallet */
  uuid: string;
  /** Inventory units of each line on this pallet, keyed by consignment line id */
  items: Record<string, { assigned: number | null }> | null;
}

/**
 * The consignment's pallets keyed by sequence number — the JSF pallet widget's
 * shape. `null` until a pallet configuration has been saved.
 */
export type PalletGroup = Record<string, PalletGroupUnit> | null;

export interface UpdatePalletGroupRequest extends PalletGroupParams {
  /** The whole new configuration: pallets left out are deleted */
  body: Record<string, PalletGroupUnit>;
}

// ─── API Slice ───────────────────────────────────────────────────────────────

/**
 * Pallet group endpoints of the consignment (`GET`/`PUT .../pallet-group`).
 *
 * Like the legacy pallet widget, every change replaces the whole configuration,
 * which allows a line to be split across pallets and taken off one of them. The
 * granular `/pallets` endpoints are not used: their assign drops a line from
 * every pallet, and their data goes stale once the group has been replaced.
 */
export const palletsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPalletGroup: builder.query<PalletGroup, PalletGroupParams>({
      query: ({ consignmentId, projectId, siteId }) => ({
        url: palletsEndpoint(`/${consignmentId}/pallet-group`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, arg) => consignmentLinesTags(arg)
    }),

    /**
     * Replace the pallet configuration. The cached group is updated right away
     * and rolled back if the save fails; the lines are refetched afterwards for
     * the capacity of new pallets. The rollback restores the whole group, which is
     * safe only because saves never overlap — go through `useUpdatePalletGroup`.
     */
    updatePalletGroup: builder.mutation<PalletGroup, UpdatePalletGroupRequest, ErrorResponse>({
      query: ({ consignmentId, projectId, siteId, body }) => ({
        url: palletsEndpoint(`/${consignmentId}/pallet-group`),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      async onQueryStarted({ consignmentId, projectId, siteId, body }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          palletsApi.util.updateQueryData('getPalletGroup', { consignmentId, projectId, siteId }, () => body)
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_result, _error, arg) => consignmentLinesTags(arg)
    })
  })
});

export const { useGetPalletGroupQuery, useUpdatePalletGroupMutation } = palletsApi;
